import PropTypes from "prop-types"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo } from "react"
import DeleteTimeOff from "../../../DeleteTimeOff"
import { useDispatch, useSelector } from "react-redux"
import { getCalendarTimeOff } from "store/calendar"
import { useTimeOffDeletion } from "../../hooks/useTimeOffDeletion"
import { TIME_OFF_BE_KEYS, getTimeOffColumns } from "pagesV2/pro/Calendar/constants"
import TimeOffWeekdays from "./TimeOffWeekdays"
import TimeOffRange from "./TimeOffRange"
import TimeOffSingleDay from "./TimeOffSingleDay"
import styles from "./index.module.css"
import { Form, Skeleton } from "antd"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import useSearchFilter from "hooks/useSearchFilter"

dayjs.extend(customParseFormat)

const TimeOffEntries = ({ type, form, setEditingTimeOffItem }) => {
  const { t } = useTranslation()
  const { timeOff } = useSelector((state) => state.calendar)
  const rawProfileId = Form.useWatch("crew_member_id", form)
  const profileId = rawProfileId === undefined ? undefined : (rawProfileId || null)
  const dispatch = useDispatch()
  const [params] = useSearchFilter({
    accessAttributes: ["range", "date"]
  })

  const dates = useMemo(() => {
    if (!params.date || !params.range) return {}
    const date = dayjs(params.date)
    if (params.range === "month") {
      return {
        start: date.startOf("month").startOf("week").format("YYYY-MM-DD"),
        end: date.endOf("month").endOf("week").format("YYYY-MM-DD")
      }
    }
    if (params.range === "week") {
      return {
        start: date.startOf("week").format("YYYY-MM-DD"),
        end: date.endOf("week").format("YYYY-MM-DD")
      }
    }
    return {
      start: date.startOf("day").format("YYYY-MM-DD"),
      end: date.endOf("day").format("YYYY-MM-DD")
    }
  }, [params.date, params.range])

  const { isDeleteModalOpen, handleDeleteTimeOff, handleCancelDeleteTimeOff, handleYesDeleteTimeOff } = useTimeOffDeletion({
    setEditingTimeOffItem,
    form,
    timeOffType: type,
    dates,
    profileId
  })

  const handleEditTimeOff = (timeOffItem) => {
    const currentCrewMemberId = form.getFieldValue("crew_member_id")
    form.resetFields()
    setEditingTimeOffItem(timeOffItem)

    const baseFields = {
      crew_member_id: currentCrewMemberId,
      notes: timeOffItem?.notes ?? timeOffItem?.note ?? "",
      recurringYearly: timeOffItem?.recurring_yearly ?? false
    }

    const toDayjsTime = (val) => {
      if (!val) return null
      const d = dayjs(val, ["hh:mm A", "hh:mm a"], true)
      return d.isValid() ? d : null
    }
    const toDayjsDate = (val) => {
      if (!val) return null
      const d = dayjs(val, ["YYYY-MM-DD", "MM/DD/YYYY"], true)
      return d.isValid() ? d : null
    }

    switch (type) {
      case TIME_OFF_BE_KEYS.weekDays:
        {
          const weekday = timeOffItem?.day
          const weekDayFields = weekday
            ? {
                selectedDays: [weekday],
                [`startTime_${weekday}`]: toDayjsTime(timeOffItem?.start_time),
                [`endTime_${weekday}`]: toDayjsTime(timeOffItem?.end_time),
                [`fullDay_${weekday}`]: !!timeOffItem?.all_day
              }
            : { selectedDays: [] }

          form.setFieldsValue({
            ...baseFields,
            timeOffType: TIME_OFF_BE_KEYS.weekDays,
            ...weekDayFields,
            weeksStartDate: toDayjsDate(timeOffItem?.season_start),
            weeksEndDate: toDayjsDate(timeOffItem?.season_end)
          })
        }
        break

      case TIME_OFF_BE_KEYS.dateRange:
        form.setFieldsValue({
          ...baseFields,
          timeOffType: TIME_OFF_BE_KEYS.dateRange,
          dateRangeFromDate: toDayjsDate(timeOffItem?.season_start),
          dateRangeToDate: toDayjsDate(timeOffItem?.season_end),
          dateRangeFromTime: toDayjsTime(timeOffItem?.start_time),
          dateRangeToTime: toDayjsTime(timeOffItem?.end_time)
        })
        break

      case TIME_OFF_BE_KEYS.singleDay:
        form.setFieldsValue({
          ...baseFields,
          timeOffType: TIME_OFF_BE_KEYS.singleDay,
          singleDayDate: toDayjsDate(timeOffItem?.season_start),
          singleDayStartTime: toDayjsTime(timeOffItem?.start_time),
          singleDayEndTime: toDayjsTime(timeOffItem?.end_time),
          singleDayFullDay: !!timeOffItem?.all_day
        })
        break
      default:
        break
    }
  }

  useEffect(() => {
    if (profileId === undefined) return
    dispatch(getCalendarTimeOff({ ui_type: type }, profileId))
  }, [type, profileId, dispatch])

  const getDropdownItems = (timeOffItem) => [
    {
      key: "editTimeOff",
      label: t("calendar.schedule_time_off.edit_time_off"),
      onClick: () => handleEditTimeOff(timeOffItem)
    },
    {
      key: "deleteTimeOff",
      label: t("calendar.schedule_time_off.delete_time_off"),
      onClick: () => handleDeleteTimeOff(timeOffItem?.id)
    }
  ]

  const columns = getTimeOffColumns(type)

  const renderTimeOffDetailsBasedOnType = (type) => {
    switch (type) {
      case TIME_OFF_BE_KEYS.weekDays:
        return <TimeOffWeekdays timeOff={timeOff?.data || []} getDropdownItems={getDropdownItems} />
      case TIME_OFF_BE_KEYS.dateRange:
        return <TimeOffRange timeOff={timeOff?.data || []} getDropdownItems={getDropdownItems} />
      case TIME_OFF_BE_KEYS.singleDay:
        return <TimeOffSingleDay timeOff={timeOff?.data || []} getDropdownItems={getDropdownItems} />
      default:
        return null
    }
  }

  return (
    <div className={styles.timeOffEntriesContainer}>
      <table className={`${styles.timeOffTable} w-full`}>
        {timeOff.data.length > 0 && (
          <thead>
            {columns?.map((column, index) => {
              const isLast = index === columns.length - 1
              return (
                <th key={column || index} className={isLast ? styles.actionColumn : ""}>
                  <Text fw={500}>{column}</Text>
                </th>
              )
            })}
          </thead>
        )}
        <Skeleton loading={timeOff?.loading} active paragraph={{ rows: 1 }} className="mt-l">
          {renderTimeOffDetailsBasedOnType(type)}
        </Skeleton>
        {isDeleteModalOpen && (
          <DeleteTimeOff open={isDeleteModalOpen} onCancel={handleCancelDeleteTimeOff} onYes={handleYesDeleteTimeOff} />
        )}
      </table>
    </div>
  )
}

TimeOffEntries.propTypes = {
  type: PropTypes.string,
  form: PropTypes.object.isRequired,
  setEditingTimeOffItem: PropTypes.func.isRequired
}

export default TimeOffEntries
